import { tournamentsAPI } from '@/services/tournaments'

const state = {
  tournaments: [],
  currentTournament: null,
  myRegistrations: [],
  registrations: [],
  loading: false,
  error: null,
  success: null,
  filters: {
    status: null,
    search: '',
    course_id: null,
    sort: '-start_date'
  }
}

const getters = {
  activeTournaments: (state) =>
    state.tournaments.filter(t => [2, 3].includes(t.status)),

  isUserRegistered: (state) => (tournamentId) =>
    state.myRegistrations.some(r => r.tournament === tournamentId && r.status !== 3),

  getTournamentById: (state) => (id) =>
    state.tournaments.find(t => t.id === id),

  getStatusLabel: () => (status) => {
    const labels = {
      1: 'Pendiente',
      2: 'Confirmado',
      3: 'Cancelado',
      4: 'En curso',
      5: 'Finalizado',
      6: 'Cancelado'
    }
    return labels[status] || 'Desconocido'
  },

  getRegistrationStatusLabel: () => (status) => {
    const labels = {
      1: 'Pendiente',
      2: 'Confirmado',
      3: 'Cancelado',
      4: 'No asistió'
    }
    return labels[status] || 'Desconocido'
  }
}

const mutations = {
  SET_TOURNAMENTS (state, tournaments) {
    state.tournaments = tournaments
  },

  SET_CURRENT_TOURNAMENT (state, tournament) {
    state.currentTournament = tournament
  },

  SET_MY_REGISTRATIONS (state, registrations) {
    state.myRegistrations = registrations
  },

  SET_REGISTRATIONS (state, registrations) {
    state.registrations = registrations
  },

  SET_LOADING (state, loading) {
    state.loading = loading
  },

  SET_ERROR (state, error) {
    state.error = error
  },

  SET_SUCCESS (state, success) {
    state.success = success
  },

  SET_FILTER (state, { key, value }) {
    state.filters[key] = value
  },

  CLEAR_FILTERS (state) {
    state.filters = {
      status: null,
      search: '',
      course_id: null,
      sort: '-start_date'
    }
  },

  CLEAR_MESSAGES (state) {
    state.error = null
    state.success = null
  },

  ADD_TOURNAMENT (state, tournament) {
    state.tournaments.push(tournament)
  },

  UPDATE_TOURNAMENT (state, tournament) {
    const index = state.tournaments.findIndex(t => t.id === tournament.id)
    if (index >= 0) {
      state.tournaments.splice(index, 1, tournament)
    }
    if (state.currentTournament?.id === tournament.id) {
      state.currentTournament = tournament
    }
  },

  REMOVE_TOURNAMENT (state, id) {
    state.tournaments = state.tournaments.filter(t => t.id !== id)
  },

  ADD_REGISTRATION (state, registration) {
    state.myRegistrations.push(registration)
  },

  UPDATE_REGISTRATION (state, registration) {
    const index = state.myRegistrations.findIndex(r => r.id === registration.id)
    if (index >= 0) {
      state.myRegistrations.splice(index, 1, registration)
    }
  },

  INCREMENT_TOURNAMENT_PLAYERS (state, tournamentId) {
    const tournament = state.tournaments.find(t => t.id === tournamentId)
    if (tournament) {
      tournament.current_players += 1
      tournament.available_slots -= 1
      tournament.is_full = tournament.available_slots === 0
    }
  }
}

const actions = {
  async fetchTournaments ({ commit, state }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const params = {}
      if (state.filters.status) params.status = state.filters.status
      if (state.filters.search) params.search = state.filters.search
      if (state.filters.course_id) params.course_id = state.filters.course_id
      if (state.filters.sort) params.ordering = state.filters.sort

      const response = await tournamentsAPI.list(params)
      commit('SET_TOURNAMENTS', response.data.results || response.data)
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al cargar torneos'
      commit('SET_ERROR', errorMsg)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchTournamentDetail ({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await tournamentsAPI.get(id)
      commit('SET_CURRENT_TOURNAMENT', response.data)
      return response.data
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al cargar torneo'
      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createTournament ({ commit }, data) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    commit('SET_SUCCESS', null)
    try {
      const response = await tournamentsAPI.create(data)
      commit('ADD_TOURNAMENT', response.data)
      commit('SET_SUCCESS', 'Torneo creado exitosamente')
      return response.data
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al crear torneo'
      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateTournament ({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    commit('SET_SUCCESS', null)
    try {
      const response = await tournamentsAPI.update(id, data)
      commit('UPDATE_TOURNAMENT', response.data)
      commit('SET_SUCCESS', 'Torneo actualizado exitosamente')
      return response.data
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al actualizar torneo'
      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteTournament ({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    commit('SET_SUCCESS', null)
    try {
      await tournamentsAPI.delete(id)
      commit('REMOVE_TOURNAMENT', id)
      commit('SET_SUCCESS', 'Torneo eliminado exitosamente')
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al eliminar torneo'
      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async registerInTournament ({ commit }, { tournamentId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    commit('SET_SUCCESS', null)
    try {
      const response = await tournamentsAPI.register(tournamentId, data)
      commit('ADD_REGISTRATION', response.data)
      commit('INCREMENT_TOURNAMENT_PLAYERS', tournamentId)
      commit('SET_SUCCESS', 'Inscripción exitosa')
      return response.data
    } catch (err) {
      const detail = err.response?.data
      let errorMsg = 'Error al inscribirse'

      if (detail?.error) {
        errorMsg = detail.error
      } else if (detail?.non_field_errors) {
        errorMsg = Array.isArray(detail.non_field_errors)
          ? detail.non_field_errors[0]
          : detail.non_field_errors
      } else {
        errorMsg = err.message || errorMsg
      }

      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async registerPlayerByRP ({ commit }, { tournamentId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    commit('SET_SUCCESS', null)
    try {
      let response
      if (data.ccslp_user_id) {
        response = await tournamentsAPI.registerCCSLPUser(tournamentId, data)
      } else if (data.guest_name) {
        response = await tournamentsAPI.registerGuest(tournamentId, data)
      } else if (data.user_id) {
        response = await tournamentsAPI.registerByAdmin(tournamentId, data)
      } else {
        throw new Error('Parámetros inválidos para la inscripción')
      }
      commit('SET_SUCCESS', 'Jugador registrado exitosamente')
      return response.data
    } catch (err) {
      const detail = err.response?.data
      let errorMsg = 'Error al registrar jugador'

      if (detail?.error) {
        errorMsg = detail.error
      } else if (detail?.non_field_errors) {
        errorMsg = Array.isArray(detail.non_field_errors)
          ? detail.non_field_errors[0]
          : detail.non_field_errors
      } else if (detail?.detail) {
        errorMsg = detail.detail
      } else {
        errorMsg = err.message || errorMsg
      }

      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchRegistrations ({ commit }, tournamentId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await tournamentsAPI.getRegistrations(tournamentId)
      commit('SET_REGISTRATIONS', response.data.registrations || response.data)
      return response.data.registrations || response.data
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al cargar inscritos'
      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async cancelRegistration ({ commit }, registrationId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    commit('SET_SUCCESS', null)
    try {
      const response = await tournamentsAPI.cancelRegistration(registrationId)
      const updatedRegistration = { ...response.data, status: 3 }
      commit('UPDATE_REGISTRATION', updatedRegistration)
      commit('SET_SUCCESS', 'Inscripción cancelada')
      return response.data
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al cancelar inscripción'
      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchMyRegistrations ({ commit }, params) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await tournamentsAPI.myRegistrations(params)
      commit('SET_MY_REGISTRATIONS', response.data.results || response.data)
      return response.data.results || response.data
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al cargar mis inscripciones'
      commit('SET_ERROR', errorMsg)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setFilter ({ commit }, { key, value }) {
    commit('SET_FILTER', { key, value })
  },

  clearFilters ({ commit }) {
    commit('CLEAR_FILTERS')
  },

  clearMessages ({ commit }) {
    commit('CLEAR_MESSAGES')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
